import { getConfig } from '@/lib/config';
import { trackEvent } from '@/lib/mixpanel';
import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
  Operation,
  createHttpLink,
  split,
} from '@apollo/client';
import { ApolloLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

const makeHttpLink = (apiUrl: string, Authorization: string) =>
  createHttpLink({
    uri: apiUrl,
    headers: {
      'Content-Type': 'application/json',
      Authorization,
    },
  });

const makeHttpGetLink = (apiUrl: string, Authorization: string) =>
  createHttpLink({
    uri: apiUrl,
    headers: { 'Content-Type': 'application/json', Authorization },
    useGETForQueries: true,
  });

const preferGet = (operation: Operation) => {
  const ctx = operation.getContext?.() ?? {};
  return ctx.preferGet === true;
};

// WebSocket subscription link. We intentionally configure resilience knobs that
// graphql-ws leaves off by default — without them the socket has no keep-alive
// (idle proxies/load balancers close it), no ack timeout (a slow upstream looks
// indistinguishable from a dead connection), and the auth header is captured
// once at client-creation time (so any reconnect after the Auth0 token expires
// fails permanently). connectionParams as a function is the documented way to
// refresh credentials on every (re)connect attempt.
//
// Per-subscription scoping (e.g. dealId) is intentionally NOT sent here —
// each subscription already passes its scoping arguments as GraphQL variables,
// which is the right layer for it and avoids the `connectionParams` snapshot
// going stale when the user navigates without closing the socket.
const KEEP_ALIVE_MS = 30_000;
const CONNECTION_ACK_WAIT_MS = 10_000;
const RETRY_ATTEMPTS = 10;

const wsLink = (
  wsUrl: string,
  getAuthorization: () => string | Promise<string>
) =>
  new GraphQLWsLink(
    createClient({
      url: wsUrl,
      lazy: true,
      keepAlive: KEEP_ALIVE_MS,
      connectionAckWaitTimeout: CONNECTION_ACK_WAIT_MS,
      retryAttempts: RETRY_ATTEMPTS,
      connectionParams: async () => ({
        Authorization: await getAuthorization(),
      }),
    })
  );

const DATE_FIELDS = new Set([
  'couponExpirationDate',
  'createdAt',
  'updatedAt',
  'startDate',
  'startAt',
  'lastViewedAt',
  'lastUpdatedAt',
]);

// Middleware pour transformer les champs DateTime en objets Date
const dateTimeMiddleware = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (response.data) {
      convertDateFields(response.data);
    }
    return response;
  });
});

// Sanitize a free-text error message before sending to analytics:
// - drop anything that looks like a querystring (often carries tokens/IDs)
// - cap length to keep cardinality bounded
const ERROR_MESSAGE_MAX_LENGTH = 200;
function sanitizeErrorMessage(message: string): string {
  return message.replace(/\?[^\s]*/g, '').slice(0, ERROR_MESSAGE_MAX_LENGTH);
}

const getOperationType = (
  operation: Operation
): 'query' | 'mutation' | 'subscription' | 'unknown' => {
  try {
    const definition = getMainDefinition(operation.query);
    if (definition.kind === 'OperationDefinition') {
      return definition.operation;
    }
  } catch {
    /* fall through */
  }
  return 'unknown';
};

// APP_GRAPHQL_ERROR is a safety net for analytics: most business errors are
// already tracked by their domain-specific *_FAILED event (DEAL_VALIDATION_FAILED,
// IMPORT_FAILED, DEAL_ITEM_CELL_EDIT_FAILED, …). Queries without a dedicated
// failure event still surface here.
//
// Subscription network errors only reach this link AFTER graphql-ws has
// exhausted its retry budget — transient WebSocket blips that auto-recover are
// filtered out by the underlying client, so what we track here is a real,
// terminal failure rather than reconnect noise.
const analyticsErrorLink = onError(
  ({ graphQLErrors, networkError, operation }) => {
    const operationName = operation.operationName || 'unknown';
    const operationType = getOperationType(operation);
    if (networkError) {
      trackEvent('APP_GRAPHQL_ERROR', {
        operationName,
        operationType,
        errorCount: 1,
        errorCodes: [],
        errorMessage: sanitizeErrorMessage(networkError.message),
        isNetworkError: true,
      });
      return;
    }
    if (graphQLErrors && graphQLErrors.length > 0) {
      const errorCodes = Array.from(
        new Set(
          graphQLErrors.map((err) =>
            typeof err.extensions?.code === 'string'
              ? err.extensions.code
              : 'unknown'
          )
        )
      );
      trackEvent('APP_GRAPHQL_ERROR', {
        operationName,
        operationType,
        errorCount: graphQLErrors.length,
        errorCodes,
        errorMessage: sanitizeErrorMessage(graphQLErrors[0].message),
        isNetworkError: false,
      });
    }
  }
);

export function convertDateFields(obj: unknown, depth = 0): void {
  if (!obj || typeof obj !== 'object' || depth > 10) return;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      convertDateFields(item, depth + 1);
    }
    return;
  }

  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (DATE_FIELDS.has(key)) {
      if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
      ) {
        (obj as Record<string, unknown>)[key] = new Date(value);
      }
    } else if (value && typeof value === 'object') {
      convertDateFields(value, depth + 1);
    }
  }
}

// Créer le cache avec configuration pour limiter la mémoire
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        deal: {
          merge(existing, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

export const makeApolloClient = (
  authorization: string,
  getAuthorization?: () => string | Promise<string>
): ApolloClient<NormalizedCacheObject> => {
  const { apiUrl, wsUrl } = getConfig();

  const httpPostLink = makeHttpLink(apiUrl, authorization);
  const httpGetLink = makeHttpGetLink(apiUrl, authorization);

  // Fall back to the snapshot token if no getter was passed (keeps existing
  // call sites working); when a getter is provided, the WebSocket pulls a
  // fresh token on every (re)connect so an expired Auth0 token can't lock the
  // socket into a permanent auth-failed state.
  const resolveAuthorization = getAuthorization ?? (() => authorization);

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink(wsUrl, resolveAuthorization),
    split(preferGet, httpGetLink, httpPostLink)
  );

  const link = ApolloLink.from([
    analyticsErrorLink,
    dateTimeMiddleware,
    splitLink,
  ]);

  return new ApolloClient({
    link,
    cache,
    assumeImmutableResults: true,
  });
};
